import { FC, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import Skeleton from 'react-loading-skeleton';
import { shorthands, Title1, tokens } from '@fluentui/react-components';
import { makeStyles } from '../../Utils/makeStylesWrapper';
import '../../styles/skeletons.css';
import {
    Search,
    FileText,
    MessageSquare,
    Users,
    MoreVertical,
    Edit,
    Share2,
    Pin,
    Copy,
    Trash2,
    LayoutGrid,
    Ellipsis,
    Share,
    PencilLine,
    PlusCircle,
    MessagesSquare,
    SquarePlus,
    SquareMinus,
} from 'lucide-react';
import { GptStatus } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { IGptConfigurationEx } from '../../store/models/GptConfigurationEx';
import { useUserPreferences, useFeature, useRootState } from '../../store/hooks';
import { useUserContext } from '../../Contexts/UserContext';
import { useConfirm } from '../../Contexts/hooks';
import { useIsMobile } from '../../Hooks/useResponsive';
import { getGptIconAsset } from '../../Utils/image';
import { PersonalGptSpecificRoute, PersonalGptRoute, buildRoute } from '../../Routes';
import { SearchInput } from '../Common/SearchInput';
import SyButton from '../Common/SyButton';
import SpinnerOverlay from '../Common/SpinnerOverlay';

const useStyles = makeStyles({
    page: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        margin: '0 auto',
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        height: '100%',
        overflow: 'hidden',
    },
    header: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '24px',
        minHeight: '40px',
        maxHeight: '40px',
        flexShrink: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        ...shorthands.padding('10px', '16px'),
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: tokens.spacingHorizontalM,
    },
    titleIcon: {
        fontSize: tokens.fontSizeBase500,
        color: tokens.colorNeutralForeground1,
        flexShrink: 0,
    },
    titleGroup: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
        minWidth: 0,
    },
    newGptButton: {
        marginLeft: 'auto',
        marginRight: '32px',
        flexShrink: 0,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        background: tokens.colorNeutralBackground3,
        ...shorthands.padding('24px', '12px', '40px', '0px'),
        ...shorthands.gap('16px'),
    },
    search: {
        paddingLeft: '20px',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: tokens.spacingVerticalXS,
        paddingLeft: '30px',
        ...shorthands.gap('5px'),
        marginBottom: '0px',
    },
    sectionHeader: {
        marginBottom: tokens.spacingVerticalS,
    },
    sectionTitle: {
        margin: `0 0 ${tokens.spacingVerticalXS} 0`,
        color: `${tokens.colorNeutralForeground1} !important`,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        ...shorthands.gap(tokens.spacingHorizontalXXL),
        '@media (max-width: 1024px)': {
            gridTemplateColumns: '1fr',
            ...shorthands.gap(tokens.spacingHorizontalL),
        },
    },
    card: {
        backgroundColor: tokens.colorNeutralBackground1,
            borderRadius: '8px',
        boxShadow: tokens.shadow4,
        transition: 'all 0.2s ease',
        cursor: 'default',
        position: 'relative',
        overflow: 'visible',

        '@media (max-width: 1024px)': {
            width: '330px',
            maxWidth: '100%'
        },
        '@media (min-width: 430px) and (max-width: 1024px)': {
            width: '370px',
            maxWidth: '100%'
        },
        '&:hover': {
            boxShadow: tokens.shadow8,
        },
    },
    cardContent: {
        padding: '0px 2px 12px 20px', // top, right, bottom, left
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('8px'),
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        ...shorthands.gap('2px'),
        position: 'relative',
        padding: '0px 2px 12px 20px', // top, right, bottom, left
        marginTop: '12px',
    },
    gptIcon: {
        fontSize: tokens.fontSizeBase500,
        color: tokens.colorBrandForeground1,
        flexShrink: 0,
        marginTop: tokens.spacingVerticalXS,
    },
    cardTitleWrapper: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        justifyContent: 'start',
        minWidth: 0,
        ...shorthands.gap('2px'),
    },
    cardTitle: {
        textDecoration: 'none',
        transition: 'color 0.2s ease',
        marginTop: '5px',
        marginBottom: '0',
        lineHeight: '1.2',
        color: tokens.colorBrandForeground1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '380px',
        '@media (max-width: 1024px)': {
            maxWidth: '215px',
        },
    },
    authorLabel: {
        marginTop: '2px',
        marginBottom: '0',
        lineHeight: '1.2',
        color: tokens.colorNeutralForeground2,
    },
    cardMenu: {
        background: 'none',
        border: 'none',
        color: tokens.colorNeutralForeground3,
        cursor: 'pointer',
        padding: '0px 10px 8px 8px', // top, right, bottom, left
        borderRadius: tokens.borderRadiusMedium,
        transition: 'color 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        flexShrink: 0,
        position: 'relative',
        zIndex: 10,
        '&:hover': {
            color: tokens.colorNeutralForeground2,
        },
        '@media (max-width: 1024px)': {
            display: 'none',
        },
    },
    cardDescription: {
        marginLeft: '0',
        marginTop: '0',
        marginBottom: '0',
    },
    cardStats: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: '0',
        ...shorthands.gap(tokens.spacingHorizontalXL),
        flexWrap: 'wrap',
    },
    cardActions: {
        display: 'flex',
        alignItems: 'flex-start',
        marginLeft: '0',
        marginTop: '5px',
        marginBottom: '7px',
        gap: `8px`,
        flexWrap: 'nowrap',

        '& > *': {
            transformOrigin: 'left',
            minWidth: 0,
            whiteSpace: 'nowrap',
            '@media (max-width: 1024px)': {
                transform: 'scale(0.8)',
            },
        },
        '& button': {
            fontWeight: 'normal',
        },
    },
    stat: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
        color: tokens.colorNeutralForeground2,
    },
    dropdown: {
        position: 'absolute',
        top: `calc(100% - ${tokens.spacingVerticalXXXL})`,
        right: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusLarge,
        boxShadow: tokens.shadow16,
        ...shorthands.padding(tokens.spacingVerticalS, '0'),
        minWidth: '200px',
        opacity: 0,
        visibility: 'hidden',
        transform: 'translateY(-4px)',
        transition: 'all 0.2s ease',
        zIndex: 1001,
        color: tokens.colorNeutralForeground1,
    },
    dropdownActive: {
        opacity: 1,
        visibility: 'visible',
        transform: 'translateY(0)',
    },
    dropdownItem: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        textDecoration: 'none',
        fontSize: tokens.fontSizeBase300,
        cursor: 'pointer',
        border: 'none',
        background: 'none',
        width: '100%',
        textAlign: 'left',
        color: tokens.colorNeutralForeground1,
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        '& svg': {
            width: '16px',
            textAlign: 'center',
            fontSize: tokens.fontSizeBase300,
        },
        '&.danger': {
            color: `${tokens.colorPaletteRedForeground1} !important`,
            '&:hover': {
                backgroundColor: tokens.colorPaletteRedBackground1,
                color: `${tokens.colorPaletteRedForeground1} !important`,
            },
        },
    },
    dropdownItemDanger: {
        color: `${tokens.colorPaletteRedForeground1} !important`,
        '&:hover': {
            backgroundColor: tokens.colorPaletteRedBackground1,
            color: `${tokens.colorPaletteRedForeground1} !important`,
        },
    },
    emptyState: {
        display: 'none',
    },
    emptyStateActive: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalM),
        textAlign: 'center',
        minHeight: '400px',
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: tokens.borderRadiusXLarge,
    },
    emptyIcon: {
        fontSize: '3rem',
        color: tokens.colorNeutralForeground3,
        marginBottom: tokens.spacingVerticalM,
        opacity: 0.5,
    },
    gptStatusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '.25rem .5rem',
        borderRadius: tokens.borderRadiusLarge,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightMedium,
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    gptStatusReady: {
        backgroundColor: 'var(--color-green-100)',
        color: 'var(--color-green-700)',
    },
    gptStatusRunning: {
        backgroundColor: 'var(--color-blue-100)',
        color: 'var(--color-blue-700)',
    },
    gptStatusBadgesContainer: {
        display: 'flex',
        gap: '2px',
        marginLeft: 'auto',
        '@media (max-width: 1024px)': {
            paddingRight: '20px',
        },
    },
});

export interface ExploreGptsViewProps {
    gpts: IGptConfigurationEx[];
    onGptClick: (gpt: IGptConfigurationEx) => void;
    onGptEdit?: (gpt: IGptConfigurationEx) => void;
    onGptTitleClick?: (gpt: IGptConfigurationEx) => void;
    onGptShare?: (gpt: IGptConfigurationEx) => void;
    onGptPin?: (gpt: IGptConfigurationEx) => void;
    onGptDuplicate?: (gpt: IGptConfigurationEx) => void;
    onGptUnsubscribe?: (gpt: IGptConfigurationEx) => void;
    onGptDelete?: (gpt: IGptConfigurationEx) => void;
    deletingGptId?: string | null;
}

export const ExploreGptsView: FC<ExploreGptsViewProps> = ({
    gpts,
    onGptClick,
    onGptEdit,
    onGptTitleClick,
    onGptShare,
    onGptPin,
    onGptDuplicate,
    onGptUnsubscribe: onGptUnsubscribe,
    onGptDelete,
    deletingGptId,
}) => {
    const styles = useStyles();
    const navigate = useNavigate();
    const { userProfile } = useUserContext();
    const { userPreferences } = useUserPreferences();
    const chatFeature = useFeature('chat');
    const confirm = useConfirm();
    const isMobile = useIsMobile();
    const [searchTerm, setSearchTerm] = useState('');
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [myGpts, setMyGpts] = useState<IGptConfigurationEx[]>([]);
    const [sharedGpts, setSharedGpts] = useState<IGptConfigurationEx[]>([]);
    const [filteredMyGpts, setFilteredMyGpts] = useState<IGptConfigurationEx[]>([]);
    const [filteredSharedGpts, setFilteredSharedGpts] = useState<IGptConfigurationEx[]>([]);
    const [, forceUpdate] = useState({});

    // Check if GPTs are still loading (no data yet)
    const isLoading = !gpts || gpts.length === 0;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Don't close if clicking the menu button or inside the dropdown
            if (activeDropdown && !target.closest(`.${styles.cardMenu}`) && !target.closest('[data-dropdown="true"]')) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeDropdown, styles.cardMenu]);

    useEffect(() => {
        // Separate GPTs into "My GPTs" and "Shared GPTs" based on IsShared property
        const myGptsList = gpts.filter((gpt) => gpt.isShared === false && gpt.isPersonal === true && !gpt.isDefault);
        const sharedGptsList = gpts.filter((gpt) => gpt.isShared === true);

        setMyGpts(myGptsList);
        setSharedGpts(sharedGptsList);
    }, [gpts]);

    useEffect(() => {
        // Filter GPTs based on search term
        const term = searchTerm.toLowerCase().trim();

        if (!term) {
            setFilteredMyGpts(myGpts);
            setFilteredSharedGpts(sharedGpts);
        } else {
            setFilteredMyGpts(
                myGpts.filter((gpt) => gpt.name?.toLowerCase().includes(term) || gpt.description?.toLowerCase().includes(term)),
            );
            setFilteredSharedGpts(
                sharedGpts.filter(
                    (gpt) => gpt.name?.toLowerCase().includes(term) || gpt.description?.toLowerCase().includes(term),
                ),
            );
        }
    }, [searchTerm, myGpts, sharedGpts]);

    const handleMenuClick = useCallback(
        (gptId: string, e: React.MouseEvent) => {
            e.stopPropagation();
            setActiveDropdown(activeDropdown === gptId ? null : gptId);
        },
        [activeDropdown],
    );

    const handleDropdownAction = useCallback((action: () => void, e: React.MouseEvent) => {
        e.stopPropagation();
        action();
        setActiveDropdown(null);
        // Force re-render after state change
        setTimeout(() => forceUpdate({}), 100);
    }, []);

    const formatUserName = useCallback((name?: string | null) => {
        if (!name) return name;
        const parts = name
            .split(',')
            .map((p) => p.trim())
            .filter((p) => p.length > 0);
        if (parts.length === 2) {
            return `${parts[1]} ${parts[0]}`;
        }
        return name;
    }, []);

    const renderGptCard = (gpt: IGptConfigurationEx, isMyGpt: boolean) => {
        const pinned = userPreferences.addedGpts?.includes(gpt.id!) ?? false;

        return (
            <article key={`${gpt.id}-${pinned}`} className={styles.card} data-gpt-name={gpt.name}>
                <SpinnerOverlay isLoading={deletingGptId === gpt.id}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitleWrapper}>
                        {isMyGpt ? (
                            <h3
                                className={`h3 link-primary ${styles.cardTitle}`}
                                onClick={(e) => {
                                    if (!isMobile) {
                                        e.stopPropagation();
                                        onGptTitleClick?.(gpt);
                                    }
                                }}
                               style={{ cursor: isMobile ? 'default' : 'pointer' }}
                                title={gpt.name}
                            >
                                {gpt.name}
                            </h3>
                        ) : (
                            <h3 className={`h3 ${styles.cardTitle}`} style={{ cursor: 'default' }} title={gpt.name}>
                                {gpt.name}
                            </h3>
                        )}
                        <span className={`paragraph-sm ${styles.authorLabel}`}>
                            @
                            {gpt.ownerFullName
                                ? gpt.ownerFullName
                                : gpt.ownerEmail
                                  ? gpt.ownerEmail
                                  : gpt.isPersonal
                                    ? (formatUserName(userProfile?.name) ?? t('exploreGpt.createdByYou', 'You'))
                                    : 'Syensqo'}
                        </span>
                    </div>

                    <div className={styles.gptStatusBadgesContainer}>
                        {gpt.status === GptStatus.Running && (
                            <span className={`${styles.gptStatusBadge} ${styles.gptStatusRunning}`}>
                                {t('exploreGpt.inProgress', 'In progress')}
                            </span>
                        )}
                        {gpt.status === GptStatus.Ready && (
                            <span className={`${styles.gptStatusBadge} ${styles.gptStatusReady}`}>
                                {t('exploreGpt.ready', 'Ready')}
                            </span>
                        )}
                    </div>
{(isMyGpt || !gpt.isPublic || chatFeature.personalGpt!.hasRightToDuplicateSharedGpt) && (
                    <button className={styles.cardMenu} aria-label='GPT menu' onClick={(e) => handleMenuClick(gpt.id!, e)}>
                        <Ellipsis size={16} />
                    </button>)}


                    <div
                        className={`${styles.dropdown} ${activeDropdown === gpt.id ? styles.dropdownActive : ''}`}
                        data-dropdown='true'
                        onClick={(e) => e.stopPropagation()}
                    >
                        {isMyGpt && onGptEdit && !isMobile && (
                            <button
                                className={styles.dropdownItem}
                                onClick={(e) => handleDropdownAction(() => onGptEdit(gpt), e)}
                            >
                                <PencilLine size={16} />
                                <span>{t('exploreGpt.edit', 'Edit')}</span>
                            </button>
                        )}
                        {isMyGpt && chatFeature.personalGpt!.hasRightToShareGpt && onGptShare && (
                            <button 
                                className={styles.dropdownItem}
                                onClick={(e) => handleDropdownAction(() => onGptShare(gpt), e)}
                            >
                                <Share size={16} />
                                <span>{t('exploreGpt.share', 'Share')}</span>
                            </button>
                        )}
                        {onGptDuplicate && ( isMyGpt || chatFeature.personalGpt!.hasRightToDuplicateSharedGpt) && (
                            <button 
                                className={styles.dropdownItem}
                                onClick={(e) => handleDropdownAction(() => onGptDuplicate(gpt), e)}
                            >
                                <Copy size={16} />
                                <span>{t('exploreGpt.duplicate', 'Duplicate to MyGPTs')}</span>
                            </button>
                        )}
                        {!isMyGpt && !gpt.isPublic ===true && onGptUnsubscribe && (
                            <button
                                className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                                onClick={(e) => handleDropdownAction(() => onGptUnsubscribe(gpt), e)}
                            >
                                <Trash2 size={16} />
                                <span>{t('exploreGpt.unsubscribeGPT', 'Unsubscribe from GPT')}</span>
                            </button>
                        )}
                        {isMyGpt && onGptDelete && (
                            <button
                                className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                                onClick={(e) => handleDropdownAction(() => onGptDelete(gpt), e)}
                            >
                                <Trash2 size={16} />
                                <span>{t('exploreGpt.delete', 'Delete')}</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className={styles.cardContent}>
                    <p className={`paragraph-sm text-clamp-2 ${styles.cardDescription}`}>{gpt.description}</p>

                    <div className={styles.cardStats}>
                        {isMyGpt ? (
                            <>
                                <span className={`paragraph-sm ${styles.stat}`}>
                                    <Users size={14} />
                                    <span>
                                        {t('exploreGpt.sharedWith', 'Shared with')} {gpt.isPublic === true ? t('exploreGpt.all', 'all') : ": " + (gpt.numberOfShares ?? 0)}
                                    </span>
                                </span>
                                <span className={`paragraph-sm ${styles.stat}`}>
                                    <MessageSquare size={14} />
                                    <span>
                                        {t('exploreGpt.myChats', 'My chats')}: {typeof gpt.numberOfChats === 'number' && !isNaN(gpt.numberOfChats) ? gpt.numberOfChats : <Skeleton width={20} height={14} inline={true} />}
                                    </span>
                                </span>
                            </>
                        ) : (
                            <span className={`paragraph-sm ${styles.stat}`}>
                                <MessageSquare size={14} />
                                <span>
                                    {t('exploreGpt.myChats', 'My chats')}: {typeof gpt.numberOfChats === 'number' && !isNaN(gpt.numberOfChats) ? gpt.numberOfChats : <Skeleton width={20} height={14} inline={true} />}
                                </span>
                            </span>
                        )}
                    </div>

                    <div className={styles.cardActions}>
                        <SyButton
                            appearance='outline'
                            icon={<MessagesSquare className='newChatIcon' />}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onGptClick(gpt);
                            }}
                            disabled={gpt.status === GptStatus.Running}
                        >
                            {t('menu.newChat', 'New Chat')}
                        </SyButton>

                        {!pinned ? (
                            <SyButton
                                appearance='ghost'
                                color='neutral'
                                icon={<SquarePlus />}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onGptPin?.(gpt);
                                }}
                                disabled={gpt.status === GptStatus.Running}
                            >
                                {t('exploreGpt.addToMyGpts', 'Add to sidebar')}
                            </SyButton>
                        ) : (
                            <SyButton
                                appearance='ghost'
                                color='neutral'
                                icon={<SquareMinus />}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onGptPin?.(gpt);
                                }}
                            >
                                {t('exploreGpt.removeFromMyGpts', 'Remove from sidebar')}
                            </SyButton>
                        )}
                    </div>
                </div>
                </SpinnerOverlay>
            </article>
        );
    };

    const onNewGptClick = useCallback(() => {
        const personalGpts = gpts.filter((gpt) => gpt.isShared === false && gpt.isPersonal === true && !gpt.isDefault);
        if (chatFeature.personalGpt?.maxNumberOfGpts == null || personalGpts.length < chatFeature.personalGpt.maxNumberOfGpts) {
            navigate(PersonalGptRoute);
        } else {
            confirm({
                show: true,
                title: t(`application.dialog.warningTitle`),
                message: t('exploreGpt.personalGpt.warning.maxGptReachedMessage'),
                confirmText: t('Ok'),
            });
        }
    }, [gpts, chatFeature, navigate, confirm]);

    const showGlobalEmptyState = searchTerm && filteredMyGpts.length === 0 && filteredSharedGpts.length === 0;
    const showMyGptsEmptyState = searchTerm && filteredMyGpts.length === 0 && !showGlobalEmptyState;
    const showSharedGptsEmptyState = searchTerm && filteredSharedGpts.length === 0 && !showGlobalEmptyState;

    // Show skeleton loading while data is being fetched
    if (isLoading) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <div className={styles.headerTitle}>
                        <div className="skeleton-rect-wrapper">
                            <Skeleton width={120} height={34} />
                        </div>
                        <div className={styles.newGptButton}>
                            <div className="skeleton-rect-wrapper">
                                <Skeleton className="skeleton-btn" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.search}>
                        <div className="skeleton-rect-wrapper">
                            <Skeleton height={40} width="100%" />
                        </div>
                    </div>

                    <section className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <div className="skeleton-rect-wrapper" style={{ marginBottom: '12px' }}>
                                <Skeleton height={28} width="200px" />
                            </div>
                            <div className="skeleton-rect-wrapper">
                                <Skeleton height={18} width="280px" />
                            </div>
                        </div>
                        <div className={styles.grid}>
                            {isMobile ? (
                                // Mobile: 1 skeleton card per row
                                [1, 2].map((idx) => (
                                    <div key={idx} className="react-loading-skeleton" style={{ height: '380px', borderRadius: '12px' }} />
                                ))
                            ) : (
                                // Desktop: 2 skeleton cards per row
                                [1, 2, 3, 4].map((idx) => (
                                    <div key={idx} className="react-loading-skeleton" style={{ height: '380px', borderRadius: '12px' }} />
                                ))
                            )}
                        </div>
                    </section>

                    <section className={styles.section} style={{ marginTop: '23px' }}>
                        <div className={styles.sectionHeader}>
                            <div className="skeleton-rect-wrapper" style={{ marginBottom: '12px' }}>
                                <Skeleton height={28} width="200px" />
                            </div>
                            <div className="skeleton-rect-wrapper">
                                <Skeleton height={18} width="320px" />
                            </div>
                        </div>
                        <div className={styles.grid}>
                            {isMobile ? (
                                // Mobile: 1 skeleton card per row
                                [1, 2].map((idx) => (
                                    <div key={idx} className="react-loading-skeleton" style={{ height: '380px', borderRadius: '12px' }} />
                                ))
                            ) : (
                                // Desktop: 2 skeleton cards per row
                                [1, 2, 3, 4].map((idx) => (
                                    <div key={idx} className="react-loading-skeleton" style={{ height: '380px', borderRadius: '12px' }} />
                                ))
                            )}
                        </div>
                    </section>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>

            <div className={styles.header}>
                <div className={styles.headerTitle}>
                    <div className={styles.titleGroup}>
                        <LayoutGrid size={20} className={styles.titleIcon} />
                        <Title1 className='settingsTitle'>{t('exploreGpt.title', 'GPTs')}</Title1>
                    </div>

                    <SyButton
                        className={styles.newGptButton}
                        appearance='primary'
                        icon={<PlusCircle size={16} />}
                        onClick={onNewGptClick}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {t('exploreGpt.createButton', 'New GPT')}
                    </SyButton>
                </div>
            </div>


            <div className={styles.content}>
                <div className={styles.search}>
                    <SearchInput
                        value={searchTerm}
                        onChange={(_, data) => setSearchTerm(data.value)}
                        onSearch={() => {}}
                        onClear={() => setSearchTerm('')}
                        placeholder={t('exploreGpt.searchPlaceholder', 'Search GPTs...')}
                        ariaLabel='Search GPTs'
                    />
                </div>

                {showGlobalEmptyState && (
                    <div className={styles.emptyStateActive}>
                        <Search className={styles.emptyIcon} />
                        <h3 className='h2'>{t('exploreGpt.noResults', 'No results found')}</h3>
                        <p className='paragraph-secondary'>
                            {t('exploreGpt.adjustSearch', 'Try adjusting your search terms or filters.')}
                        </p>
                    </div>
                )}

                {!showGlobalEmptyState && (
                    <>
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <h2 className={`h2 ${styles.sectionTitle}`}>
                                    {t('exploreGpt.myGpts', 'My GPTs')} - {chatFeature.personalGpt?.maxNumberOfGpts != null 
                                        ? `${myGpts.length}/${chatFeature.personalGpt.maxNumberOfGpts}` 
                                        : myGpts.length}
                                </h2>
                                <p className='paragraph-sm'>
                                    {chatFeature.personalGpt?.maxNumberOfGpts != null
                                        ? t(
                                            'exploreGpt.myGptsDescription',
                                            'GPTs where you have edit rights. Maximum of {{max}} GPTs.',
                                            { max: chatFeature.personalGpt.maxNumberOfGpts },
                                        )
                                        : t(
                                            'exploreGpt.myGptsDescriptionUnlimited',
                                            'GPTs where you have edit rights. No GPTs limit.',
                                        )}
                                </p>
                            </div>
                            {showMyGptsEmptyState ? (
                                <div
                                    className={styles.emptyStateActive}
                                    style={{
                                        minHeight: '300px',
                                        padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalM}`,
                                    }}
                                >
                                    <Search className={styles.emptyIcon} />
                                    <h3 className='h2'>{t('exploreGpt.noResults', 'No results found')}</h3>
                                    <p className='paragraph-secondary'>
                                        {t('exploreGpt.adjustSearch', 'Try adjusting your search terms.')}
                                    </p>
                                </div>
                            ) : (
                                <div className={styles.grid}>{filteredMyGpts.map((gpt) => renderGptCard(gpt, true))}</div>
                            )}
                        </section>

                        {(filteredSharedGpts.length > 0 || showSharedGptsEmptyState) && (
                            <section className={styles.section} style={{ marginTop: '23px' }}>
                                <div className={styles.sectionHeader}>
                                    <h2 className={`h2 ${styles.sectionTitle}`}>{t('exploreGpt.sharedGpts', 'Shared GPTs')}</h2>
                                    <p className='paragraph-sm'>
                                        {t(
                                            'exploreGpt.sharedGptsDescription',
                                            'You need to duplicate these GPTs to your GPTs to be able to customize and edit.',
                                        )}
                                    </p>
                                </div>
                                {showSharedGptsEmptyState ? (
                                    <div
                                        className={styles.emptyStateActive}
                                        style={{
                                            minHeight: '300px',
                                            padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalM}`,
                                        }}
                                    >
                                        <Search className={styles.emptyIcon} />
                                        <h3 className='h2'>{t('exploreGpt.noResults', 'No results found')}</h3>
                                        <p className='paragraph-secondary'>
                                            {t('exploreGpt.adjustSearch', 'Try adjusting your search terms.')}
                                        </p>
                                    </div>
                                ) : (
                                    <div className={styles.grid}>{filteredSharedGpts.map((gpt) => renderGptCard(gpt, false))}</div>
                                )}
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ExploreGptsView;
